import type { GameAction, LaneId } from "@fate-kingdom/engine";

export type AlphaPlacementAction = Extract<
  GameAction,
  | { readonly type: "found-land" }
  | { readonly type: "develop-land" }
  | { readonly type: "deploy-commander" }
  | { readonly type: "replace-commander" }
  | { readonly type: "deploy-army" }
>;

export type AlphaCardDragStatus = "idle" | "pressed" | "dragging" | "dropping" | "cancelled";

export interface AlphaCardDragState {
  readonly status: AlphaCardDragStatus;
  readonly pointerId: number | null;
  readonly cardInstanceId: string | null;
  readonly pointerType: string | null;
  readonly originX: number;
  readonly originY: number;
  readonly currentX: number;
  readonly currentY: number;
  readonly previousX: number;
  readonly previousY: number;
  readonly previousTimestamp: number;
  readonly velocityX: number;
  readonly velocityY: number;
  readonly offsetX: number;
  readonly offsetY: number;
  readonly originRect: Readonly<{
    x: number;
    y: number;
    width: number;
    height: number;
  }> | null;
  readonly visualPhase: "idle" | "lift" | "travel" | "snap" | "return";
  readonly dropDestinationRect: Readonly<{
    x: number;
    y: number;
    width: number;
    height: number;
  }> | null;
  readonly hoveredLaneId: LaneId | null;
  readonly legalLaneIds: readonly LaneId[];
  readonly actionByLane: ReadonlyMap<LaneId, AlphaPlacementAction>;
}

export const IDLE_ALPHA_CARD_DRAG_STATE: AlphaCardDragState = {
  status: "idle",
  pointerId: null,
  cardInstanceId: null,
  pointerType: null,
  originX: 0,
  originY: 0,
  currentX: 0,
  currentY: 0,
  previousX: 0,
  previousY: 0,
  previousTimestamp: 0,
  velocityX: 0,
  velocityY: 0,
  offsetX: 0,
  offsetY: 0,
  originRect: null,
  visualPhase: "idle",
  dropDestinationRect: null,
  hoveredLaneId: null,
  legalLaneIds: [],
  actionByLane: new Map(),
};

export function isAlphaPlacementAction(action: GameAction): action is AlphaPlacementAction {
  return (
    action.type === "found-land" ||
    action.type === "develop-land" ||
    action.type === "deploy-commander" ||
    action.type === "replace-commander" ||
    action.type === "deploy-army"
  );
}

export function alphaPlacementCardInstanceId(action: AlphaPlacementAction): string {
  switch (action.type) {
    case "found-land":
    case "develop-land":
      return action.cardInstanceId;
    case "deploy-commander":
    case "replace-commander":
      return action.commanderCardInstanceId;
    case "deploy-army":
      return action.armyCardInstanceId;
  }
}

export function alphaPlacementActionKey(action: AlphaPlacementAction): string {
  return `${action.type}:${action.playerId}:${action.laneId}:${alphaPlacementCardInstanceId(action)}`;
}

export function alphaPlacementActionsByLane(
  cardInstanceId: string,
  actions: readonly GameAction[],
): ReadonlyMap<LaneId, AlphaPlacementAction> {
  const actionByLane = new Map<LaneId, AlphaPlacementAction>();
  for (const action of actions) {
    if (
      !isAlphaPlacementAction(action) ||
      alphaPlacementCardInstanceId(action) !== cardInstanceId
    ) {
      continue;
    }
    actionByLane.set(action.laneId, action);
  }
  return actionByLane;
}

export function alphaPlacementLabel(action: AlphaPlacementAction): string {
  switch (action.type) {
    case "found-land":
      return "Found Land";
    case "develop-land":
      return "Develop Land";
    case "deploy-commander":
      return "Deploy Commander";
    case "replace-commander":
      return "Replace Commander";
    case "deploy-army":
      return "Station Army";
  }
}
